import React, { useContext, useState } from "react";
import { styled } from "@mui/material/styles";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CloseIcon from "@mui/icons-material/Close";
import { DiscoveryContext } from "../../luminaire/BrandAudit/discovery-context";
import { getCleanQuestion, getColorForScore } from "../../utils/helpers";
import { ENTITY_LABELS, getEntityLabel } from "../../utils/labelMapping";
import { OVERVIEW_LLM_LABELS } from "../../utils/constant";

// Dark tooltip styling for composite per-LLM breakdown (CategoryAnalysis By LLM mode only)
const COMPOSITE_TOOLTIP_SX = {
  bgcolor: "#1e2340",
  fontSize: "0.73rem",
  borderRadius: "10px",
  padding: "8px 12px",
  boxShadow: "0 6px 20px rgba(0,0,0,0.28)",
  "& .MuiTooltip-arrow": { color: "#1e2340" },
};

// Per-LLM score breakdown shown when hovering a composite cell
function PerLLMScoreTooltip({ entries }) {
  if (!entries?.length) return null;
  return (
    <div style={{ minWidth: 140 }}>
      {entries.map(({ llm, value }, i) => (
        <div key={i} style={{ display: "flex", justifyContent: "space-between", gap: 14, fontSize: "0.72rem", marginBottom: i < entries.length - 1 ? 3 : 0 }}>
          <span style={{ color: "#a8c4f0", fontWeight: 600 }}>{OVERVIEW_LLM_LABELS[llm] || llm}</span>
          <span style={{ opacity: 0.9 }}>{Number(value).toFixed(2)}</span>
        </div>
      ))}
    </div>
  );
}

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.common.black,
    color: theme.palette.common.white,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: theme.palette.action.hover,
  },
  "&:last-child td, &:last-child th": {
    border: 0,
  },
}));

// Build rows from a single LLM's data
function buildSingleLLMRows(discoveryCtx, selectedLLM, category, market, selectedPersona) {
  const hasMainBrand = (discoveryCtx.competitors?.["default"]?.length ?? 0) > 0;
  const competitorNames = discoveryCtx?.competitors?.[category]?.map((c) => c.name) ?? [];
  const competitorStartIndex = hasMainBrand ? 1 : 0;
  const allRows = [];
  const competitorRows = [];
  const addedRowNames = new Set();

  const brandData = discoveryCtx.brand_scores[selectedLLM]?.[category]?.[market]?.[selectedPersona]?.[0]?.extras;
  if (hasMainBrand && brandData?.favorabilities?.length > 0) {
    brandData.favorabilities.forEach((fav) => {
      if (fav?.name && !addedRowNames.has(fav.name)) {
        allRows.push(fav);
        addedRowNames.add(fav.name);
      }
    });
  }
  competitorNames.forEach((compName, index) => {
    const compData = discoveryCtx.brand_scores[selectedLLM]?.[category]?.[market]?.[selectedPersona]?.[competitorStartIndex + index]?.extras;
    if (compData?.favorabilities?.length > 0) {
      compData.favorabilities.forEach((fav) => {
        if (fav?.name && !addedRowNames.has(fav.name)) {
          allRows.push(fav);
          addedRowNames.add(fav.name);
          competitorRows.push(fav);
        }
      });
    }
  });
  return { allRows, competitorRows, hasMainBrand, competitorStartIndex, brandData };
}

// Compute composite rows by averaging sentiment scores across multiple LLMs
function buildCompositeLLMRows(discoveryCtx, multiLLMs, category, market, selectedPersona) {
  const llms = (multiLLMs || []).filter(Boolean);
  if (llms.length === 0) return { allRows: [], competitorRows: [] };

  const perLLM = llms.map(llm => buildSingleLLMRows(discoveryCtx, llm, category, market, selectedPersona));

  // Gather all unique brand names preserving order
  const brandOrder = [];
  const seenNames = new Set();
  perLLM.forEach(({ allRows }) => {
    allRows.forEach(row => {
      if (row?.name && !seenNames.has(row.name)) {
        brandOrder.push(row.name);
        seenNames.add(row.name);
      }
    });
  });

  // Determine all sentiment keys present
  const sentimentKeySet = new Set();
  perLLM.forEach(({ allRows }) => {
    allRows.forEach(row => Object.keys(row).filter(k => k.startsWith("sentiment")).forEach(k => sentimentKeySet.add(k)));
  });
  const sentimentKeysList = [...sentimentKeySet].sort();

  const hasScore = perLLM.some(({ allRows }) => allRows.some(r => r.score !== undefined));

  // Determine which brands are "competitors" (not main brand) — use first LLM that has data
  const mainBrandName = perLLM.find(d => d.hasMainBrand)?.allRows?.[0]?.name;

  const compositeRows = brandOrder.map(brandName => {
    // Keep the originating LLM alongside each brand's row so we can show a per-LLM hover breakdown
    const entries = perLLM
      .map(({ allRows }, llmIdx) => ({ llm: llms[llmIdx], row: allRows.find(r => r.name === brandName) }))
      .filter(e => e.row);
    const row = { name: brandName, _perLLM: {} };
    sentimentKeysList.forEach(key => {
      const withLLM = entries
        .map(e => ({ llm: e.llm, value: parseFloat(e.row[key]) }))
        .filter(v => !isNaN(v.value));
      row[key] = withLLM.length > 0 ? (withLLM.reduce((a, b) => a + b.value, 0) / withLLM.length).toFixed(2) : null;
      row._perLLM[key] = withLLM;
    });
    const avgWithLLM = entries
      .map(e => ({ llm: e.llm, value: parseFloat(e.row.average) }))
      .filter(v => !isNaN(v.value));
    row.average = avgWithLLM.length > 0 ? (avgWithLLM.reduce((a, b) => a + b.value, 0) / avgWithLLM.length).toFixed(2) : null;
    row._perLLM.average = avgWithLLM;
    if (hasScore) {
      const scoreWithLLM = entries
        .map(e => ({ llm: e.llm, value: parseFloat(e.row.score) }))
        .filter(v => !isNaN(v.value));
      row.score = scoreWithLLM.length > 0 ? (scoreWithLLM.reduce((a, b) => a + b.value, 0) / scoreWithLLM.length).toFixed(2) : undefined;
      row._perLLM.score = scoreWithLLM;
    }
    return row;
  });

  const competitorRows = compositeRows.filter(r => r.name !== mainBrandName);
  return { allRows: compositeRows, competitorRows };
}

export default function AverageSentimentScores({
  category,
  market,
  selectedPersona,
  selectedLLM,
  competitors,
  ctx = null,
  compareMode,
  multiLLMs,
}) {
  const _defaultCtx = useContext(DiscoveryContext);
  const discoveryCtx = ctx || _defaultCtx;
  const isPropensity = discoveryCtx?.isPropensityToBuy?.() ?? (discoveryCtx?.brand_scores?.rbiType === "propensityToBuy");
  const mapTitle = (t) => discoveryCtx?.mapMetricTitle?.(t) ?? t;

  const [questionModal, setQuestionModal] = useState(null);

  const isCompositeLLM = compareMode === "llm" && Array.isArray(multiLLMs) && multiLLMs.length > 1;

  // Build rows: composite (average across LLMs) or single-LLM
  let allRows, competitorRows, hasMainBrand, competitorStartIndex, brandData;
  if (isCompositeLLM) {
    ({ allRows, competitorRows } = buildCompositeLLMRows(discoveryCtx, multiLLMs, category, market, selectedPersona));
    hasMainBrand = (discoveryCtx.competitors?.["default"]?.length ?? 0) > 0;
    competitorStartIndex = hasMainBrand ? 1 : 0;
    brandData = discoveryCtx.brand_scores[selectedLLM]?.[category]?.[market]?.[selectedPersona]?.[0]?.extras;
  } else {
    hasMainBrand = (discoveryCtx.competitors?.["default"]?.length ?? 0) > 0;
    const competitorNames = discoveryCtx?.competitors?.[category]?.map((c) => c.name) ?? [];
    competitorStartIndex = hasMainBrand ? 1 : 0;
    allRows = [];
    competitorRows = [];
    const addedRowNames = new Set();

    brandData = discoveryCtx.brand_scores[selectedLLM]?.[category]?.[market]?.[selectedPersona]?.[0]?.extras;
    if (hasMainBrand && brandData?.favorabilities?.length > 0) {
      brandData.favorabilities.forEach((fav) => {
        if (fav?.name && !addedRowNames.has(fav.name)) {
          allRows.push(fav);
          addedRowNames.add(fav.name);
        }
      });
    }
    competitorNames.forEach((compName, index) => {
      const compData = discoveryCtx.brand_scores[selectedLLM]?.[category]?.[market]?.[selectedPersona]?.[competitorStartIndex + index]?.extras;
      if (compData?.favorabilities?.length > 0) {
        compData.favorabilities.forEach((fav) => {
          if (fav?.name && !addedRowNames.has(fav.name)) {
            allRows.push(fav);
            addedRowNames.add(fav.name);
            competitorRows.push(fav);
          }
        });
      }
    });
  }

  // Calculate Competitor Average row
  if (competitorRows.length > 0) {
    const competitorAverageRow = { name: "Competitor Average" };

    const sentimentKeys = Object.keys(competitorRows[0]).filter((key) =>
      key.startsWith("sentiment")
    );

    sentimentKeys.forEach((key) => {
      const sum = competitorRows.reduce((acc, row) => {
        const value = parseFloat(row[key]);
        return acc + (isNaN(value) ? 0 : value);
      }, 0);
      competitorAverageRow[key] = (sum / competitorRows.length).toFixed(2);
    });

    const avgSum = competitorRows.reduce((acc, row) => {
      const value = parseFloat(row.average);
      return acc + (isNaN(value) ? 0 : value);
    }, 0);
    competitorAverageRow.average = (avgSum / competitorRows.length).toFixed(2);

    if (competitorRows[0].score !== undefined) {
      const scoreSum = competitorRows.reduce((acc, row) => {
        const value = parseFloat(row.score);
        return acc + (isNaN(value) ? 0 : value);
      }, 0);
      competitorAverageRow.score = (scoreSum / competitorRows.length).toFixed(2);
    }

    allRows.push(competitorAverageRow);
  }

  if (allRows.length === 0) return null;

  // --- Question text extraction for info icons ---
  // Find the first brand's sentimentMeta and its associated name for brand placeholder replacement
  let questionMeta = null;
  let questionBrandName = null;
  const competitorNames = discoveryCtx?.competitors?.[category]?.map((c) => c.name) ?? [];

  if (hasMainBrand && brandData?.sentimentMeta?.length > 0) {
    questionMeta = brandData.sentimentMeta;
    questionBrandName = allRows[0]?.name ?? null;
  }
  if (!questionMeta) {
    competitorNames.forEach((compName, index) => {
      if (questionMeta) return;
      const compExtras =
        discoveryCtx.brand_scores[selectedLLM]?.[category]?.[market]?.[selectedPersona]?.[competitorStartIndex + index]?.extras;
      if (compExtras?.sentimentMeta?.length > 0) {
        questionMeta = compExtras.sentimentMeta;
        questionBrandName = compName;
      }
    });
  }

  function getCommonQuestion(index) {
    if (!questionMeta || index >= questionMeta.length) return null;
    const raw = questionMeta[index]?.[0]?.question;
    const cleaned = getCleanQuestion(raw);
    if (!cleaned) return null;
    if (!questionBrandName) return cleaned;
    const escapedName = questionBrandName.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    return cleaned.replace(new RegExp(escapedName, "gi"), "{{ brand }}");
  }

  // Use any row with favorabilities to determine column count
  const templateFavorabilities =
    (hasMainBrand ? brandData?.favorabilities : null) ??
    (() => {
      for (let i = competitorStartIndex; i < competitorStartIndex + competitorNames.length; i++) {
        const d = discoveryCtx.brand_scores[selectedLLM]?.[category]?.[market]?.[selectedPersona]?.[i]?.extras?.favorabilities;
        if (d?.length) return d;
      }
      return null;
    })();

  const sentimentColumnCount = templateFavorabilities?.length > 0
    ? Object.keys(templateFavorabilities[0]).filter((key) => key.startsWith("sentiment")).length
    : 0;

  // Prepare data for color coding
  const isExclude = true; // Set to false to include Competitor Average in color comparison
  
  // Filter rows for color comparison based on isExclude flag
  const rowsForColorComparison = isExclude 
    ? allRows.filter(row => row.name !== "Competitor Average")
    : allRows;
  
  // Get all values for each sentiment column for color comparison
  const sentimentColumnValues = {};
  for (let i = 0; i < sentimentColumnCount; i++) {
    const colKey = `sentiment${i + 1}`;
    sentimentColumnValues[colKey] = rowsForColorComparison
      .map(row => row[colKey])
      .filter(val => val !== undefined && val !== null);
  }

  // Get all average values for color comparison
  const averageValues = rowsForColorComparison
    .map(row => row.average)
    .filter(val => val !== undefined && val !== null);

  return (
    <>
      <TableContainer>
        <Table sx={{ minWidth: 800 }} aria-label="average sentiment scores">
          <TableHead>
            <StyledTableRow>
              <StyledTableCell>{`${getEntityLabel(discoveryCtx.type)} Name`}</StyledTableCell>
              {Array.from({ length: sentimentColumnCount }).map((_, i) => {
                const qText = getCommonQuestion(i);
                return (
                  <StyledTableCell align="right" key={i}>
                    <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "flex-end", gap: 3 }}>
                      Question {i + 1}
                      {qText && (
                        <Tooltip title="View question" arrow>
                          <InfoOutlinedIcon
                            fontSize="small"
                            sx={{ fontSize: 14, cursor: "pointer", opacity: 0.75, "&:hover": { opacity: 1 } }}
                            onClick={() => setQuestionModal({ number: i + 1, text: qText })}
                          />
                        </Tooltip>
                      )}
                    </span>
                  </StyledTableCell>
                );
              })}
              <StyledTableCell align="right">Avg</StyledTableCell>
              {templateFavorabilities?.[0]?.score !== undefined && (
                <StyledTableCell align="right">Score</StyledTableCell>
              )}
            </StyledTableRow>
          </TableHead>
          <TableBody>
            {allRows?.map((row, idx) => {
              const isCompetitorAverage = row.name === "Competitor Average";
              return (
                <StyledTableRow
                  key={`${row.name}-${idx}`}
                  sx={{
                    "&:last-child td, &:last-child th": { border: 0 },
                    backgroundColor: isCompetitorAverage ? "#e3f2fd" : "inherit",
                    fontWeight: isCompetitorAverage ? "bold" : "normal",
                  }}
                >
                  <StyledTableCell
                    component="th"
                    scope="row"
                    sx={{ fontWeight: isCompetitorAverage ? "bold" : "normal" }}
                  >
                    {row.name}
                  </StyledTableCell>
                  {Array.from({ length: sentimentColumnCount }).map((_, i) => {
                    const colKey = `sentiment${i + 1}`;
                    const cellValue = row[colKey];
                    const cellColor = (!isCompetitorAverage || !isExclude)
                      ? getColorForScore(cellValue, sentimentColumnValues[colKey])
                      : 'inherit';
                    // In composite (By LLM) mode, hovering a cell reveals each LLM's score
                    const perLLMEntries = row._perLLM?.[colKey];
                    const cell = (
                      <StyledTableCell
                        align="right"
                        key={i}
                        sx={{
                          fontWeight: isCompetitorAverage ? "bold" : "normal",
                          color: cellColor,
                          cursor: isCompositeLLM && perLLMEntries?.length > 0 ? "pointer" : "inherit",
                        }}
                      >
                        {cellValue}
                      </StyledTableCell>
                    );
                    return isCompositeLLM && perLLMEntries?.length > 0 ? (
                      <Tooltip
                        key={i}
                        title={<PerLLMScoreTooltip entries={perLLMEntries} />}
                        arrow
                        placement="top"
                        componentsProps={{ tooltip: { sx: COMPOSITE_TOOLTIP_SX } }}
                      >
                        {cell}
                      </Tooltip>
                    ) : cell;
                  })}
                  {(() => {
                    const avgEntries = row._perLLM?.average;
                    const avgCell = (
                      <StyledTableCell
                        align="right"
                        sx={{
                          fontWeight: isCompetitorAverage ? "bold" : "normal",
                          color: (!isCompetitorAverage || !isExclude)
                            ? getColorForScore(row.average, averageValues)
                            : 'inherit',
                          cursor: isCompositeLLM && avgEntries?.length > 0 ? "pointer" : "inherit",
                        }}
                      >
                        {row.average}
                      </StyledTableCell>
                    );
                    return isCompositeLLM && avgEntries?.length > 0 ? (
                      <Tooltip
                        title={<PerLLMScoreTooltip entries={avgEntries} />}
                        arrow
                        placement="top"
                        componentsProps={{ tooltip: { sx: COMPOSITE_TOOLTIP_SX } }}
                      >
                        {avgCell}
                      </Tooltip>
                    ) : avgCell;
                  })()}
                  {row.score !== undefined && (
                    <StyledTableCell
                      align="right"
                      sx={{ fontWeight: isCompetitorAverage ? "bold" : "normal" }}
                    >
                      {row?.score}
                    </StyledTableCell>
                  )}
                </StyledTableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog
        open={!!questionModal}
        onClose={() => setQuestionModal(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pb: 1 }}>
          <span>Question {questionModal?.number}</span>
          <IconButton size="small" onClick={() => setQuestionModal(null)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
            {questionModal?.text || "Question text not available"}
          </Typography>
        </DialogContent>
      </Dialog>
    </>
  );
}
